<script lang="ts">
import { lateinit } from 'tg-commons'
import { bindEvents, LatLng, type CircleEventMap, type CircleOptions, type CircleOverlay } from 'tg-map-core'
import { defineComponent, toRaw } from 'vue'
import { eventProp, optionalProp, requiredProp, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../../utils/vue-utils'
import MapMixin from '../map-mixin'
type TgCircleEmits = {
  'update:center': LatLng,
  'update:radius': number,
}
export default defineComponent({
  name: 'tg-circle',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: {
    center: requiredProp(LatLng),
    radius: requiredProp(Number),
    clickable: optionalProp(Boolean),
    editable: optionalProp(Boolean),
    strokeColor: optionalProp(String),
    strokeOpacity: optionalProp(Number),
    strokeWeight: optionalProp(Number),
    visible: optionalProp(Boolean),
    zIndex: optionalProp(Number),
    fillColor: optionalProp(String),
    fillOpacity: optionalProp(Number),
    'onUpdate:center': eventProp(),
    'onUpdate:radius': eventProp(),
  } satisfies Props<CircleOptions> & EventProps<TgCircleEmits>,
  emits: undefined as any as EventEmits<CircleEventMap & TgCircleEmits>,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      overlay: lateinit<CircleOverlay>(),
      emittedRadius: undefined as number | undefined,
      emittedCenter: undefined as LatLng | undefined,
    }
  },
  watch: {
    center(value) {
      if (this.emittedCenter != null) {
        const center = this.emittedCenter
        this.emittedCenter = undefined
        if (center === toRaw(value)) {
          // 防止emit出的center被设置回来
          return
        }
      }
      this.overlay.setCenter(value)
    },
    radius(value) {
      if (this.emittedRadius != null) {
        const radius = this.emittedRadius
        this.emittedRadius = undefined
        if (radius === value) {
          // 防止emit出的radius被设置回来
          return
        }
      }
      this.overlay.setRadius(value)
    },
    clickable() {
      this.recreate()
    },
    editable(value) {
      this.overlay.setEditable(value)
    },
    strokeColor(value) {
      this.overlay.setStrokeColor(value)
    },
    strokeOpacity(value) {
      this.overlay.setStrokeOpacity(value)
    },
    strokeWeight(value) {
      this.overlay.setStrokeWeight(value)
    },
    fillColor(value) {
      this.overlay.setFillColor(value)
    },
    fillOpacity(value) {
      this.overlay.setFillOpacity(value)
    },
    visible(value) {
      this.overlay.setVisible(value)
    },
    zIndex(value) {
      this.overlay.setZIndex(value)
    },
  },
  onCreate() {
    this.overlay = this.$map.createCircle(this)
    this.$map.addOverlay(this.overlay)
    bindEvents(this.attrs.listeners, this.overlay, this.$emit)
    if (this.attrs.listenerProps['update:center']) {
      this.overlay.addEventListener('center-changed', () => {
        this.$emit('update:center', this.emittedCenter = this.overlay.getCenter())
      })
    }
    if (this.attrs.listenerProps['update:radius']) {
      this.overlay.addEventListener('radius-changed', () => {
        this.$emit('update:radius', this.emittedRadius = this.overlay.getRadius())
      })
    }
  },
  onDestroy() {
    this.$map.removeOverlay(this.overlay)
  },
})
</script>
